import {
  Component,
  model,
  input,
  output,
  effect,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DrawerModule } from 'primeng/drawer';
import { IconComponent } from '../icon/icon.component';
import type { User } from 'src/app/models/user.model';
import type { MenuOption } from 'src/app/models/menu-option.model';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { environment as ENV } from './../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    IconComponent,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    TooltipModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {
  readonly version = ENV.appVersion;

  readonly opened = model<boolean>(true);

  readonly user = input.required<User>();
  readonly options = input.required<Array<MenuOption>>();
  readonly showOptions = input<boolean>(false);

  readonly isCollapsed = output<boolean>();
  readonly logOut = output<void>();

  private readonly emitCollapsedEffect = effect(() => {
    this.isCollapsed.emit(this.opened());
  });

  readonly enhancedUser = computed(() => {
    const user = this.user();
    return {
      ...user,
      cleanRoles: user.roles
        ?.map((role) => role.replace('_', ' '))
        .join(' ') || [''],
      initials: this.getAvatarInitials(),
      countryEmoji: this.getFlagEmoji()
    };
  });

  logout(ev: Event): void {
    ev.stopPropagation();
    this.logOut.emit();
  }

  private getAvatarInitials(): string {
    const fullName = this.user()?.name;
    if (!fullName) return '';
    const fullNameSplit = fullName.trim().split(' ');
    const validParts = [fullNameSplit[0]];
    if (fullNameSplit.length > 1) {
      validParts.push(fullNameSplit[fullNameSplit.length - 1]);
    }
    const initials = validParts
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
    return initials;
  }

  // Convert a 2-letter country code to the corresponding flag emoji
  private getFlagEmoji(): string {
    const countryCode = this.user()?.country;
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }
}
